/**
 * @file paths.ts
 * @description Адреса витрины для переадресаций 301 (phase19.16, партия Р3в,
 * v0.19.64; site-builder.md SB131; db-design-r3v §2.1): приведение адреса
 * «откуда», единая таблица маршрутов витрины `SITE_ROUTE_TABLE`,
 * классификация адреса и список адресов, запрещённых страницам конструктора
 * (`RESERVED_PAGE_SLUGS` — выводится из таблицы, закрывает TD-478).
 *
 * Зачем в shared: адрес «откуда» приводят одинаково три стороны — api при
 * записи правила, витрина в proxy при поиске правила и кабинет при вводе.
 * Разойдись они в одной мелочи (регистр, хвостовой «/», кодировка
 * кириллицы) — правило молча перестанет срабатывать.
 *
 * Таблица маршрутов повторяет папки `apps/web/src/app`. Новая папка там без
 * строки здесь роняет тест-страж (`route-table.guard.test.ts`): иначе
 * страница конструктора или переадресация перекроет новый маршрут.
 *
 * Модуль без зависимостей от Node и браузера (`URL` есть и там, и там).
 */

import { deepFreeze } from "../site-theme/deep-freeze.js";
import { matchesSitePathRule, type SitePathMatch } from "../site-consent/site-path-rules.js";

// ── Приведение адреса ────────────────────────────────────────────────────

/** Предел длины приведённого адреса (с `%`-кодировкой), знаков. */
export const SITE_REDIRECT_PATH_MAX = 500;

/** Результат приведения: путь, строка запроса без «?», хост из полного адреса. */
export interface NormalizedSitePath {
  /** `/catalog/%D1%87%D0%B0%D0%B9` — нижний регистр, без хвостового «/» (кроме корня). */
  readonly path: string;
  /** Строка запроса без «?» как была введена; пусто — запроса нет. */
  readonly query: string;
  /**
   * Хост, если ввели полный адрес `https://…` (нижний регистр); иначе null.
   * Свой ли это адрес магазина, решает вызывающий (api знает домены магазина).
   */
  readonly host: string | null;
}

/**
 * Есть ли управляющие символы (U+0000…U+001F, U+007F): браузер выбрасывает
 * таб и перевод строки из адреса («/%09/evil.com» → «//evil.com»).
 * Повтор `hasControlChars` из apps/web/src/lib/site/site-preview-cookie.ts
 * (витрина shared не экспортирует обратно — копия, не импорт).
 */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/** Один сегмент: раскодировать, проверить, NFC + нижний регистр, закодировать обратно. */
function normalizeSegment(segment: string): string | null {
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Битая %-последовательность — адрес не наш.
    return null;
  }
  // `%2F` и `%5C` внутри сегмента меняют число сегментов после раскодирования;
  // `.`/`..` браузер сворачивает сам — в правиле они означали бы другой адрес.
  if (decoded.includes("/") || decoded.includes("\\") || hasControlChars(decoded)) return null;
  if (decoded === "." || decoded === "..") return null;
  try {
    // Кириллица — строчными (SB131: регистр адреса не важен), затем в %-кодировку,
    // как её присылает браузер: так «откуда» в базе и путь запроса совпадают побайтно.
    return encodeURIComponent(decoded.normalize("NFC").toLowerCase());
  } catch {
    // Одиночный суррогат — encodeURIComponent бросает URIError.
    return null;
  }
}

/**
 * Приводит адрес «откуда» (и путь запроса в proxy) к одному виду:
 *   - только путь: полный адрес `https://host/…` разбирается, хост — в `host`;
 *     иначе адрес обязан начинаться с «/»;
 *   - `#…` отбрасывается, `?…` отделяется в `query`;
 *   - `//` схлопывается, хвостовой «/» снимается (корень остаётся «/»);
 *   - каждый сегмент раскодируется и кодируется заново (кириллица — в `%`),
 *     регистр — нижний, Unicode — NFC;
 *   - отказ (null): `\`, управляющие символы (до и после раскодирования),
 *     битая `%`-кодировка, `%2F` в сегменте, `.`/`..`, длина > 500.
 * Те же запреты, что `isSafeSitePath` витрины (SB138,
 * apps/web/src/lib/site/site-preview-cookie.ts), плюс регистр и кодировка.
 */
export function normalizeSiteRedirectPath(raw: string | null | undefined): NormalizedSitePath | null {
  if (typeof raw !== "string") return null;
  // Пробелы по краям — частая ошибка при вставке из таблицы; таб и перевод
  // строки не срезаем — их ловит проверка управляющих символов.
  let input = raw.replace(/^ +| +$/gu, "");
  if (input.length === 0 || input.includes("\\") || hasControlChars(input)) return null;

  let host: string | null = null;
  if (/^https?:\/\//iu.test(input)) {
    let url: URL;
    try {
      url = new URL(input);
    } catch {
      return null;
    }
    host = url.hostname.toLowerCase();
    input = `${url.pathname}${url.search}`;
  } else if (!input.startsWith("/")) {
    return null;
  }

  const hashAt = input.indexOf("#");
  if (hashAt >= 0) input = input.slice(0, hashAt);
  const queryAt = input.indexOf("?");
  const rawPath = queryAt >= 0 ? input.slice(0, queryAt) : input;
  const query = queryAt >= 0 ? input.slice(queryAt + 1) : "";

  const segments: string[] = [];
  for (const segment of rawPath.split("/")) {
    if (segment.length === 0) continue; // `//` и хвостовой «/»
    const normalized = normalizeSegment(segment);
    if (normalized === null) return null;
    segments.push(normalized);
  }
  const path = `/${segments.join("/")}`;
  if (path.length > SITE_REDIRECT_PATH_MAX) return null;
  return { path, query, host };
}

/** Произвольная база для разбора: адрес свой, только если origin не сменился. */
const PATH_PROBE_ORIGIN = "http://x";

/**
 * Безопасный адрес цели на своём сайте: начинается с одного «/», без `\` и
 * управляющих символов, разбор относительно базы не уводит на другой origin
 * (защита от open redirect: `//evil.com`, `/\evil.com`, `/%09/evil.com`).
 * Повтор `isSafeSitePath` из apps/web/src/lib/site/site-preview-cookie.ts.
 */
export function isSafeSiteTargetPath(raw: string | null | undefined): raw is string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\") || hasControlChars(raw)) {
    return false;
  }
  try {
    const url = new URL(raw, PATH_PROBE_ORIGIN);
    return url.origin === PATH_PROBE_ORIGIN && url.pathname.startsWith("/") && !url.pathname.startsWith("//");
  } catch {
    return false;
  }
}

// ── Таблица маршрутов витрины ────────────────────────────────────────────

/**
 * Вид маршрута:
 *   - `service` — служебный: «откуда» быть не может, proxy пропускает слой
 *     переадресаций без чтения памяти (ЛК, оформление, документы, файлы, API);
 *   - `section` — корень раздела (`/`, `/catalog`, `/blog`, `/product`):
 *     «откуда» быть не может, а вложенные адреса — могут;
 *   - `entity` — адрес сущности по шаблону (`/product/[slug]`): занят, только
 *     если сущность живая (решает api, «живое важнее»).
 */
export type SiteRouteKind = "service" | "section" | "entity";

/** Чья сущность живёт по адресу шаблона. */
export type SitePathOwnerType =
  | "page"
  | "product"
  | "catalog-category"
  | "article"
  | "blog-category"
  | "blog-tag"
  | "blog-author";

interface SiteRouteBase {
  /** Адрес в нотации маршрутов витрины: `/lk`, `/[slug]`, `/product/[slug]/reviews`. */
  readonly route: string;
  /** Зачем строка (для читателя таблицы). */
  readonly note: string;
}

export interface SiteFixedRoute extends SiteRouteBase {
  readonly kind: "service" | "section";
  /** Как сравнивать: адрес и всё под ним, только адрес, основа файла-иконки. */
  readonly match: SitePathMatch;
}

export interface SiteEntityRoute extends SiteRouteBase {
  readonly kind: "entity";
  readonly owner: SitePathOwnerType;
  /** Хвост после адреса сущности (`/product/x/reviews` — отзывы товара). */
  readonly suffix?: "reviews";
}

export type SiteRouteEntry = SiteFixedRoute | SiteEntityRoute;

const service = (route: string, note: string, match: SitePathMatch = "segment"): SiteFixedRoute => ({
  route,
  kind: "service",
  match,
  note,
});
const section = (route: string, note: string): SiteFixedRoute => ({ route, kind: "section", match: "exact", note });
const entity = (route: string, owner: SitePathOwnerType, note: string, suffix?: "reviews"): SiteEntityRoute =>
  suffix ? { route, kind: "entity", owner, suffix, note } : { route, kind: "entity", owner, note };

/**
 * Единая таблица маршрутов витрины (`apps/web/src/app`, сверено 2026-10-25).
 * Порядок проверки в `classifySitePath`: служебные → разделы → сущности;
 * внутри сущностей — от точных к общим не важно: шаблоны разной длины.
 */
export const SITE_ROUTE_TABLE: readonly SiteRouteEntry[] = deepFreeze<SiteRouteEntry[]>([
  // Служебные маршруты витрины (папки app/)
  service("/api", "API витрины: ревалидация кэша, в dev — прокси к API"),
  service("/lk", "личный кабинет покупателя, ссылки из писем с ключами"),
  service("/checkout", "оформление заказа и «Спасибо»"),
  service("/order-confirm", "подтверждение заказа по ссылке из письма"),
  service("/legal", "юридические документы магазина (SB141)"),
  service("/offer", "оферта — прежний адрес документа"),
  service("/privacy", "политика ПДн — прежний адрес документа"),
  service("/preview", "живой предпросмотр конструктора"),
  service("/site-preview", "включение предпросмотра вида сайта (Р3а)"),
  service("/site-staff", "пропуск сотрудника на закрытый сайт (SB138)"),
  service("/site-closed", "заглушка «Сайт закрыт» (SB138)"),
  service("/site-consent", "запись выбора посетителя о cookie (Р3г)"),
  service("/site-not-found", "маяк журнала «Не найдено» (SB148)"),
  service("/healthz", "проверка живости контейнера"),
  service("/platform", "раздел платформы Commora (phase19.28)"),
  service("/domain-not-connected", "заглушка «Домен не подключён» (phase19.28)"),
  service("/shop-disabled", "заглушка «Магазин отключён» (phase19.28)"),
  service("/robots.txt", "robots.txt магазина", "exact"),
  // Файлы метаданных Next (app/sitemap.ts, app/manifest.ts) и иконки
  service("/sitemap.xml", "карта сайта", "exact"),
  service("/manifest.webmanifest", "манифест приложения", "exact"),
  service("/favicon.ico", "значок сайта", "exact"),
  service("/icon", "значок Next (icon, icon1.png)", "stem"),
  service("/apple-icon", "значок Apple Next", "stem"),
  service("/blog/feed.xml", "RSS-лента блога", "exact"),
  // Статика Next и public/
  service("/_next", "сборка Next: скрипты, стили, оптимизатор картинок"),
  service("/fonts", "файлы шрифтов (public/fonts)"),
  service("/images", "картинки витрины (public/images)"),
  // Зарезервированные адреса без маршрута (SB131 перечисляет их явно)
  service("/admin", "кабинет — на своём адресе, адрес держим в резерве"),
  service("/cart", "корзина — панель, адрес держим в резерве"),

  // Разделы: корни всегда живые
  section("/", "главная"),
  section("/catalog", "каталог"),
  section("/blog", "блог"),
  section("/product", "корень адресов товаров (своей страницы нет)"),
  section("/blog/category", "корень адресов рубрик"),
  section("/blog/tag", "корень адресов тегов"),
  section("/blog/author", "корень адресов авторов"),

  // Сущности: адрес занят, пока сущность живая
  entity("/[slug]", "page", "страница конструктора"),
  entity("/product/[slug]", "product", "товар"),
  entity("/product/[slug]/reviews", "product", "отзывы о товаре", "reviews"),
  entity("/catalog/[categorySlug]", "catalog-category", "категория каталога"),
  entity("/blog/[slug]", "article", "статья блога"),
  entity("/blog/category/[slug]", "blog-category", "рубрика блога"),
  entity("/blog/tag/[slug]", "blog-tag", "тег блога"),
  entity("/blog/author/[slug]", "blog-author", "автор блога"),
]);

// ── Классификация ────────────────────────────────────────────────────────

/** Кто живёт по адресу страничного вида; `other` — маршрута нет (старый адрес прошлого сайта). */
export type SitePathOwner =
  | { readonly type: "page"; readonly slug: string }
  | { readonly type: "product"; readonly slug: string; readonly suffix?: "reviews" }
  | { readonly type: Exclude<SitePathOwnerType, "page" | "product">; readonly slug: string }
  | { readonly type: "other" };

/**
 * Итог классификации:
 *   - `service` — переадресовать нельзя, слой proxy пропускает без чтения
 *     памяти (`route` null — адрес не разобрался: ведём себя как со служебным);
 *   - `section` — корень раздела, «откуда» быть не может;
 *   - `page` — страничный адрес: сущность по шаблону или «чужой» адрес;
 *     может быть «откуда», если по нему сейчас ничего живого (решает api).
 */
export type SitePathClass =
  | { readonly kind: "service"; readonly route: string | null }
  | { readonly kind: "section"; readonly route: string }
  | { readonly kind: "page"; readonly route: string | null; readonly owner: SitePathOwner };

const FIXED_ROUTES = SITE_ROUTE_TABLE.filter((r): r is SiteFixedRoute => r.kind !== "entity");
const ENTITY_ROUTES = SITE_ROUTE_TABLE.filter((r): r is SiteEntityRoute => r.kind === "entity");

const isDynamicSegment = (segment: string): boolean => segment.startsWith("[") && segment.endsWith("]");

/** Значение динамического сегмента, если путь подходит под шаблон сущности. */
function matchEntityRoute(route: SiteEntityRoute, segments: readonly string[]): string | null {
  const pattern = route.route.split("/").filter((s) => s.length > 0);
  if (pattern.length !== segments.length) return null;
  let slug: string | null = null;
  for (let i = 0; i < pattern.length; i += 1) {
    const expected = pattern[i] as string;
    const actual = segments[i] as string;
    if (isDynamicSegment(expected)) slug = actual;
    else if (expected !== actual) return null;
  }
  return slug;
}

/**
 * Классифицирует адрес по `SITE_ROUTE_TABLE`. Принимает любой вид адреса
 * (сам приводит через `normalizeSiteRedirectPath`, query отбрасывает).
 */
export function classifySitePath(rawPath: string): SitePathClass {
  const normalized = normalizeSiteRedirectPath(rawPath);
  if (normalized === null) return { kind: "service", route: null };
  const { path } = normalized;

  // Служебные раньше разделов: `/blog/feed.xml` иначе ушёл бы в статью.
  for (const route of FIXED_ROUTES) {
    if (route.kind === "service" && matchesSitePathRule(path, { path: route.route, match: route.match })) {
      return { kind: "service", route: route.route };
    }
  }
  for (const route of FIXED_ROUTES) {
    if (route.kind === "section" && matchesSitePathRule(path, { path: route.route, match: route.match })) {
      return { kind: "section", route: route.route };
    }
  }
  const segments = path.split("/").filter((s) => s.length > 0);
  for (const route of ENTITY_ROUTES) {
    const raw = matchEntityRoute(route, segments);
    if (raw === null) continue;
    // Сегмент уже прошёл приведение — раскодирование не бросит.
    const slug = decodeURIComponent(raw);
    if (route.owner === "product") {
      const owner: SitePathOwner = route.suffix ? { type: "product", slug, suffix: route.suffix } : { type: "product", slug };
      return { kind: "page", route: route.route, owner };
    }
    return { kind: "page", route: route.route, owner: { type: route.owner, slug } };
  }
  return { kind: "page", route: null, owner: { type: "other" } };
}

/** Может ли адрес быть «откуда» по таблице маршрутов (живое содержимое проверяет api). */
export function isRedirectableSitePath(rawPath: string): boolean {
  return classifySitePath(rawPath).kind === "page";
}

// ── Адреса, запрещённые страницам конструктора (TD-478) ──────────────────

/**
 * Резерв без маршрута — адреса будущих разделов, чтобы не выкупать их у
 * контента (были в api reserved-slugs.ts до Р3в).
 */
export const SITE_RESERVED_FUTURE_SLUGS: readonly string[] = Object.freeze([
  "account",
  "search",
  "login",
  "register",
  "p",
  "pages",
]);

/**
 * Список адресов, которые не может занять страница конструктора (`/<slug>`):
 * первый сегмент всех служебных маршрутов и разделов таблицы, `home`
 * (системная главная) и резерв будущих разделов. Надмножество прежнего
 * `RESERVED_PAGE_SLUGS` из apps/api/src/site-pages/reserved-slugs.ts.
 */
export const RESERVED_PAGE_SLUG_LIST: readonly string[] = Object.freeze(
  [
    ...new Set([
      "home",
      ...FIXED_ROUTES.map((r) => r.route.split("/").filter((s) => s.length > 0)[0]).filter(
        (s): s is string => typeof s === "string",
      ),
      ...SITE_RESERVED_FUTURE_SLUGS,
    ]),
  ].sort(),
);

/** То же множеством — для проверки slug при записи страницы. */
export const RESERVED_PAGE_SLUGS: ReadonlySet<string> = new Set(RESERVED_PAGE_SLUG_LIST);
